// C-66: 「保有クーポン」の分類と並び（純関数 / 顧客 LIFF の一覧と管理画面の顧客詳細が共有）。
//
// 分類の真実源を 1 箇所に置く理由 — 顧客が見る「使える」と管理者が見る「使える」が別々の判定で
// 食い違うと、「お客様は使えると言うのに画面では期限切れ」の問い合わせが構造的に起きる。
// 判定材料は `coupon_usages`（個人票）と `coupons`（マスタ）の 2 つで、利用期間は
// **個人票の窓 > マスタの静的値**（`applyCouponRedeem` / CouponPage と同じ優先）。
import { resolveMaxRedeemsPerUser, } from './promoTypes.js';
/** 一覧の「使える」区画に出す状態（それ以外は折りたたみ側）。 */
export const ACTIVE_COUPON_HOLDING_STATES = [
    'usable',
    'not_yet',
    'not_claimed',
    'claim_not_yet',
];
export function isActiveCouponHoldingState(state) {
    return ACTIVE_COUPON_HOLDING_STATES.includes(state);
}
/**
 * 店頭使用（redeem）回数を解釈する（旧 doc = `redeemCount` 無しの `redeemed` は 1 回）。
 * 分類と「あと ◯ 回」の表示が別々に fallback すると 1 回ずれるので、ここ 1 箇所で決める。
 */
export function resolveCouponRedeemCount(usage) {
    if (typeof usage.redeemCount === 'number')
        return usage.redeemCount;
    return usage.status === 'redeemed' ? 1 : 0;
}
/** 表示に使う利用期間（個人票の窓 > マスタ）。両方無ければ両端 undefined。 */
export function resolveCouponHoldingWindow(usage, coupon) {
    const usableFrom = usage.usableFrom ?? coupon?.usableFrom;
    const usableUntil = usage.usableUntil ?? coupon?.usableUntil;
    return {
        ...(usableFrom ? { usableFrom } : {}),
        ...(usableUntil ? { usableUntil } : {}),
    };
}
/**
 * 保有クーポン 1 件を分類する（純関数）。
 *
 * 時刻比較は JST ISO8601 の文字列比較（このリポの時刻規約 / `applyCouponRedeem` と同じ）。
 * 旧 doc（`redeemCount` 無し）は `status==='redeemed'` なら 1 回とみなす（`fetchMyCouponUsage` と同じ）。
 */
export function classifyCouponHolding(usage, coupon, nowIso) {
    if (!coupon)
        return 'unknown';
    // Callable は `status !== 'active'` を最初に拒否する（claim: not_active / redeem: not_active）。
    if (coupon.status !== 'active')
        return 'ended';
    if (usage.status === 'delivered') {
        // 未取得: 取得期間（マスタの validFrom / validUntil）で判定する。利用期間はまだ関係ない。
        if (typeof coupon.validUntil === 'string' && nowIso > coupon.validUntil)
            return 'expired';
        if (typeof coupon.validFrom === 'string' && nowIso < coupon.validFrom)
            return 'claim_not_yet';
        return 'not_claimed';
    }
    const maxRedeems = resolveMaxRedeemsPerUser(coupon);
    if (resolveCouponRedeemCount(usage) >= maxRedeems)
        return 'redeemed';
    const { usableFrom, usableUntil } = resolveCouponHoldingWindow(usage, coupon);
    if (usableUntil && nowIso > usableUntil)
        return 'expired';
    if (usableFrom && nowIso < usableFrom)
        return 'not_yet';
    return 'usable';
}
const STATE_ORDER = {
    usable: 0,
    not_yet: 1,
    not_claimed: 2,
    claim_not_yet: 3,
    redeemed: 4,
    expired: 5,
    ended: 6,
    unknown: 7,
};
/**
 * 一覧の並び（純関数 / 入力を変異させない）。
 *
 * - 状態順: usable → not_yet → not_claimed → claim_not_yet → redeemed → expired → ended → unknown
 * - `usable` 同士は利用期限の**近い順**（無期限は末尾）— 先に切れるものを上に
 * - それ以外の同状態は `usedAt` 降順（新しいものが上）
 */
export function sortCouponHoldings(items) {
    return [...items].sort((a, b) => {
        const so = STATE_ORDER[a.state] - STATE_ORDER[b.state];
        if (so !== 0)
            return so;
        if (a.state === 'usable') {
            const au = resolveCouponHoldingWindow(a.usage, a.coupon).usableUntil;
            const bu = resolveCouponHoldingWindow(b.usage, b.coupon).usableUntil;
            if (au && bu && au !== bu)
                return au < bu ? -1 : 1;
            if (au && !bu)
                return -1;
            if (!au && bu)
                return 1;
        }
        return a.usage.usedAt < b.usage.usedAt ? 1 : a.usage.usedAt > b.usage.usedAt ? -1 : 0;
    });
}
/** ISO8601（JST）を `YYYY-MM-DD HH:mm` に整形（CouponPage / 管理画面のタイムラインと同方式）。 */
export function formatCouponTime(iso) {
    const datePart = iso.slice(0, 10);
    const timePart = iso.slice(11, 16);
    return timePart ? `${datePart} ${timePart}` : datePart;
}
/** 利用期間を `開始 〜 終了` / `〜 終了` / `開始 〜` に整形。両方無ければ空文字。 */
export function formatCouponUsablePeriod(from, until) {
    const f = from ? formatCouponTime(from) : '';
    const u = until ? formatCouponTime(until) : '';
    if (f && u)
        return `${f} 〜 ${u}`;
    if (u)
        return `〜 ${u}`;
    if (f)
        return `${f} 〜`;
    return '';
}
//# sourceMappingURL=couponHolding.js.map